import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, PhoneIcon } from '@heroicons/react/24/outline';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Floating Glass Header */}
      <header className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 w-[95%] max-w-6xl`}>
        <div className={`
          relative rounded-full border shadow-2xl backdrop-blur-xl transition-all duration-500
          ${isScrolled
            ? 'bg-white/70 py-3 px-6 border-white/40 shadow-lg'
            : 'bg-white/5 py-4 px-8 border-white/10 shadow-none hover:bg-white/10'
          }
        `}>
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div className={`relative w-12 h-12 overflow-hidden rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ease-out ring-2 ${isScrolled ? 'bg-naqd-primary ring-white/50' : 'bg-white ring-white/20'}`}>
                <img
                  src="/assets/logo.png"
                  alt="NAQD"
                  className={`h-8 w-auto object-contain transition-all duration-300 ${isScrolled ? 'brightness-0 invert' : ''}`}
                />
              </div>
            </Link>

            {/* Desktop Navigation - Centered Pill */}
            <nav className="hidden lg:flex items-center justify-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-300
                    ${isActive(item.href)
                      ? 'text-white bg-naqd-primary shadow-lg'
                      : isScrolled
                        ? 'text-naqd-text hover:bg-naqd-primary/10 hover:text-naqd-primary'
                        : 'text-white hover:bg-white/20 hover:text-white'
                    }
                  `}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href="tel:+919074286763"
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isScrolled ? 'bg-naqd-secondary/10 text-naqd-secondary hover:bg-naqd-secondary hover:text-white' : 'bg-white/20 text-white hover:bg-white hover:text-naqd-primary'}`}
                title="Call Us"
              >
                <PhoneIcon className="w-5 h-5" />
              </a>
              <Link
                to="/contact"
                className={`rounded-full px-6 py-2.5 text-xs shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 font-bold tracking-wide ${isScrolled ? 'bg-naqd-primary text-white hover:shadow-naqd-primary/30' : 'bg-white text-naqd-primary hover:bg-gray-100 hover:shadow-white/30'}`}
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isScrolled ? 'bg-white/50 text-naqd-primary hover:bg-naqd-primary hover:text-white' : 'bg-white/20 text-white hover:bg-white hover:text-naqd-primary'}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`
          absolute top-full left-0 right-0 mt-4 p-4 rounded-3xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl transform transition-all duration-300 origin-top
          ${isMenuOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'}
        `}>
          <nav className="flex flex-col space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                  ${isActive(item.href)
                    ? 'bg-naqd-primary text-white shadow-md'
                    : 'text-naqd-text hover:bg-gray-50'
                  }
                `}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="h-px bg-gray-100 my-2"></div>
            <div className="flex items-center justify-between px-2">
              <a
                href="tel:+919074286763"
                className="flex items-center space-x-2 text-sm font-medium text-naqd-primary"
              >
                <PhoneIcon className="w-4 h-4" />
                <span>+91 90742 86763</span>
              </a>
              <Link
                to="/contact"
                className="btn-primary rounded-full px-6 py-2 text-xs"
                onClick={() => setIsMenuOpen(false)}
              >
                Get Started
              </Link>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;